import { useCallback, useEffect, useState } from "react";
import { ApiError, api, type OperatorHealth } from "../api";

/**
 * The operator dashboard: one screen answering "is anything wrong anywhere?"
 * across every tenant. Shown only to operator users; a client user never
 * reaches it, and the API would return 404 even if they tried.
 *
 * Deliberately plain. This is a working surface for one admin, not a client
 * deliverable, so it favours density and legibility over polish: the counts up
 * top, the anomalies that need action, then the raw run log underneath.
 */
export function Operator() {
  const [data, setData] = useState<OperatorHealth | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .operatorHealth()
      .then(setData)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Could not load"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = useCallback(
    async (id: string) => {
      setBusy(id);
      try {
        await api.resolveAnomaly(id);
        // Drop it locally rather than refetch, so the list does not flicker.
        setData((d) =>
          d ? { ...d, anomalies: d.anomalies.filter((a) => a.id !== id) } : d,
        );
      } catch {
        load(); // fall back to the server's truth if the acknowledge failed
      } finally {
        setBusy(null);
      }
    },
    [load],
  );

  if (error) return <div className="error banner">{error}</div>;
  if (!data) return <div className="empty">Loading…</div>;

  const healthy = data.open_errors === 0 && data.open_warnings === 0 && data.failed_runs_24h === 0;

  return (
    <div className="operator">
      <section className="op-stats">
        <Stat label="Open errors" value={data.open_errors} tone={data.open_errors ? "bad" : "ok"} />
        <Stat
          label="Open warnings"
          value={data.open_warnings}
          tone={data.open_warnings ? "warn" : "ok"}
        />
        <Stat
          label="Failed runs (24h)"
          value={data.failed_runs_24h}
          tone={data.failed_runs_24h ? "bad" : "ok"}
        />
        <button className="ghost small-btn op-refresh" onClick={load}>
          Refresh
        </button>
      </section>

      {healthy && data.anomalies.length === 0 ? (
        <div className="op-allclear">All pipelines healthy. Nothing needs attention.</div>
      ) : (
        <section className="op-section">
          <h2>Open anomalies</h2>
          {data.anomalies.length === 0 ? (
            <div className="muted small">None open.</div>
          ) : (
            <ul className="op-anomalies">
              {data.anomalies.map((a) => (
                <li key={a.id} className={`op-anomaly ${a.severity}`}>
                  <span className={`op-sev ${a.severity}`}>{a.severity}</span>
                  <div className="op-anomaly-body">
                    <div className="op-anomaly-head">
                      <strong>{a.tenant}</strong>
                      <span className="muted small"> · {a.dataset}</span>
                      <span className="op-kind">{a.kind.replace(/_/g, " ")}</span>
                    </div>
                    <div className="op-anomaly-msg">{a.message}</div>
                    <div className="muted small">{formatTime(a.detected_at)}</div>
                  </div>
                  <button
                    className="ghost small-btn"
                    disabled={busy === a.id}
                    onClick={() => resolve(a.id)}
                  >
                    {busy === a.id ? "…" : "Resolve"}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="op-section">
        <h2>Recent runs</h2>
        <div className="op-table-scroll">
          <table className="op-runs">
            <thead>
              <tr>
                <th>When</th>
                <th>Tenant</th>
                <th>Dataset</th>
                <th>Source</th>
                <th className="num">Parsed</th>
                <th className="num">Ins</th>
                <th className="num">Upd</th>
                <th className="num">Skip</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.runs.map((r, i) => (
                <tr key={i} className={r.ok ? "" : "run-bad"}>
                  <td className="nowrap">{formatTime(r.started_at)}</td>
                  <td>{r.tenant}</td>
                  <td>{r.dataset}</td>
                  <td>{r.source}</td>
                  <td className="num">{r.rows_parsed}</td>
                  <td className="num">{r.inserted}</td>
                  <td className="num">{r.updated}</td>
                  <td className="num">{r.skipped}</td>
                  <td>
                    {r.ok ? (
                      <span className="op-ok">ok</span>
                    ) : (
                      <span className="op-fail" title={r.error ?? ""}>
                        failed
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {data.runs.length === 0 && (
                <tr>
                  <td colSpan={9} className="muted small">
                    No runs recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className={`op-stat ${tone}`}>
      <div className="op-stat-value">{value}</div>
      <div className="op-stat-label">{label}</div>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
